import type { Keepsake } from '../../life/hut';

// Keepsakes that come with the page, by name: ?keepsake=<name> on a garden's address plays one arriving on its Muse's
// shelf (gardenUi.ts arrive). The binoculars: the owner's drawing, found by sam-3.1 and made in 3D by Tripo
// (npm run keepsake); as made, its lenses look along +x, so a quarter turn sets them to the front.
const BUNDLED: Readonly<Record<string, Omit<Keepsake, 'name'>>> = {
  binoculars: { url: new URL('../../assets/keepsakes/binoculars.glb', import.meta.url).href, square: -Math.PI / 2 },
};

/** The keepsake an address names (?keepsake=), if it comes with the page. */
export function bundledKeepsake(name: string | null): Keepsake | null {
  const made = name ? BUNDLED[name] : undefined;
  return name && made ? { name, ...made } : null;
}

/**
 * The bundled keepsakes' arrivals filmed (on a browser with a GPU, from the live page at a side browser's size), for
 * ?keepsake=<name>&film: a browser that can't draw the world (a cloud browser with no GPU shows a blank page) plays the
 * film instead.
 */
const FILMS: Readonly<Record<string, string>> = {
  binoculars: new URL('../../assets/keepsakes/binoculars-arrival.webm', import.meta.url).href,
};

/** The film an address asks for (?keepsake=<name>&film), if there is one. */
export function filmOf(query: URLSearchParams): string | null {
  const name = query.get('keepsake');
  return query.has('film') && name ? (FILMS[name] ?? null) : null;
}

/** Play a keepsake's arrival filmed, filling the window, and nothing else (no world is made); titled for the garden's Muse. */
export function playFilm(url: string, root: HTMLElement, garden: string | null): void {
  for (const el of root.ownerDocument.querySelectorAll<HTMLElement>('#scene, #ui')) el.style.display = 'none';
  const video = root.ownerDocument.createElement('video');
  Object.assign(video, { src: url, muted: true, autoplay: true, playsInline: true, preload: 'auto' });
  video.setAttribute('aria-label', 'A keepsake arriving on Muse’s shelf');
  Object.assign(video.style, { position: 'fixed', inset: '0', width: '100%', height: '100%', objectFit: 'cover', background: '#2F7CC4' });
  root.append(video);
  void video.play().catch(() => undefined);
  if (garden?.startsWith('/api/muses/')) {
    fetch(`${garden}/garden`)
      .then((r) => (r.ok ? (r.json() as Promise<{ muse?: { name?: string } }>) : null))
      .then((s) => {
        if (s?.muse?.name) document.title = `${s.muse.name}’s Garden`;
      })
      .catch(() => undefined);
  }
}
